import { createHash } from 'crypto';

/**
 * Deterministic 0–99 bucketing for the Paddle rollout ramp (Phase 2, #3910).
 *
 * The Paddle-for-new-users rollout is widened gradually: each user is hashed
 * into a stable bucket `0..99`, and Paddle is enabled only for the buckets
 * **below** a configured percentage (`bucket < rolloutPercent`). This helper is
 * the hashing half — `selectWebProvider` applies the `< rolloutPercent` test.
 * See design-docs/paddle-integration.md ("Phase 2 — Paddle for new users in production (the percentile ramp)").
 *
 * Three properties this function is built (and unit-tested) for:
 *  - **Deterministic** — the same identifier always maps to the same bucket, so
 *    a user sees the same provider on every page load (no flip-flop).
 *  - **Roughly uniform** — across many identifiers the buckets spread evenly, so
 *    "percent of users below P" is approximately P. The ramp number means what
 *    it says.
 *  - **Monotonic ramp** — because the routing test is strictly `<`, raising the
 *    percent only ever ADDS buckets; a user already in Paddle never flips back.
 *
 * It is a pure function with no I/O, so it is identical on client and server.
 */

/** Number of buckets. A bucket is `0..(BUCKET_COUNT - 1)`, i.e. `0..99`. */
export const BUCKET_COUNT = 100;

/**
 * Map an identifier to a stable bucket in `[0, 99]`.
 *
 * The identifier is hashed **verbatim**, so the caller must pass an already-
 * canonical value: normalize an email with `normalizeEmail()` before calling
 * (trim + lowercase, so case/whitespace variants share a bucket); a uid or an
 * anonymous browser id is already canonical and is passed as-is. (The
 * `selectWebProvider` dispatcher does this normalization before calling.)
 *
 * An empty / missing identifier hashes deterministically too (it is just the
 * hash of the empty string), so the function never throws; callers that want a
 * different policy for "no identifier" handle that before calling.
 */
export function bucketForIdentifier(identifier: string | undefined): number {
  const canonical = identifier || '';

  // SHA-256 → first 8 hex chars → integer → mod 100. Eight hex digits is a
  // 32-bit value (0 .. 0xffffffff), far larger than 100, so the modulo's bias
  // toward the low buckets is negligible (well under one part in 40 million) —
  // immaterial for a rollout ramp.
  const hashHex = createHash('sha256').update(canonical).digest('hex');
  const first8 = hashHex.slice(0, 8);
  const asInt = parseInt(first8, 16);
  return asInt % BUCKET_COUNT;
}
